import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { EventListener } from '../../../../../lib/web/components/event-listener';
import { Input } from '../../../../../lib/web/components/input';
import { QueryString } from '../../../../../lib/web/core/query-string';
import { Str } from '../../../../../lib/web/core/str';
import { DictionaryObject } from '../../../../../lib/web/core/types';
import { ToastService } from '../../../../../lib/web/services/toast.service';
import { ApiService } from '../../../services/api.service';
import { SessionService } from '../../../services/session.service';
import { ValidationService } from '../../../services/validation.service';
import { CountrySelectorComponent } from '../../atoms/country-selector/country-selector.component';
import { SwitchBoxComponent } from '../../atoms/switch-box/switch-box.component';
import { TabComponent } from '../../atoms/tab/tab.component';
import { ChangePasswordConfirmComponent } from '../../molecules/change-password-confirm/change-password-confirm.component';
import { ChangePasswordComponent } from '../../molecules/change-password/change-password.component';
import { ChangePostalCodeComponent } from '../../molecules/change-postal-code/change-postal-code.component';
import { ChangeProfileComponent } from '../../molecules/change-profile/change-profile.component';
import { CreatePinComponent } from '../../molecules/create-pin/create-pin.component';
import { EnterPinComponent } from '../../molecules/enter-pin/enter-pin.component';

@Component({
    selector: '.p-profile'
})
export class ProfileComponent extends ComponentBase<HTMLElement> {

    @ChildRef('#profile-image')
    private _fileElement: HTMLInputElement = null;
    @ChildRef('.p-profile__image')
    private _pictureElement: HTMLImageElement = null;
    @Input('country')
    private _country: number = null;
    @Input('postalCode')
    private _postalCode: string = null;
    @ChildRef()
    private _changePassword: ChangePasswordComponent = null;
    @ChildRef()
    private _changePasswordConfirm: ChangePasswordConfirmComponent = null;
    @ChildRef()
    private _changeProfile: ChangeProfileComponent = null;
    @ChildRef()
    private _changePostalCode: ChangePostalCodeComponent = null;
    @ChildRef()
    private _countrySelector: CountrySelectorComponent = null;
    @ChildRef()
    private _createPin: CreatePinComponent = null;
    @ChildRef()
    private _enterPin: EnterPinComponent = null;
    @ChildRef()
    private _tab: TabComponent = null;

    private _verticalTabsQuery: MediaQueryList = window.matchMedia('(max-width: 1024px)');

    public constructor(node: HTMLElement,
        private _toastService: ToastService, 
        private _apiService: ApiService, 
        private _sessionService: SessionService,
        private _validationService: ValidationService) {
        super(node);
    }

    public onInit(): void {
        this._toastService.hide('pendingProfileData');
        this.addCustomEventListener('profile-updated', (data: { fullname: string }) => {
            this.setInnerHTML(data.fullname, '.p-profile__user-name');
        });
        if (this._pictureElement.src) {
            this.removeClass('u-hidden-important', '.p-profile__image');
            this.removeClass('u-hidden-important', '.p-profile__remove-picture');
        }
        else {
            this.removeClass('u-hidden-important', '.p-profile__signed-icon');
        }
        const paths: string[] = location.pathname.split('/');
        if (paths.last() == 'change-password') {
            this.onChangePassword();
        }
        this.updateTabOrientation(this._verticalTabsQuery);
        this._verticalTabsQuery.addEventListener('change', e => this.updateTabOrientation(e));

        const tabId: string = new URLSearchParams(location.search).get('tab') || 'data';
        if (tabId) {
            this._tab.setSelected(Str.slugToCamel(tabId));
        }
    }

    @ClickListener('.p-profile__remove-picture')
    public onRemovePicture() {
        this.removePicture();
    }

    @EventListener('change', '#profile-image')
    public onPictureChange(): void {
        this.loadPicture();
    }

    @EventListener('selection-changed', '.a-tab')
    public onTabSelectionChanged(e: CustomEvent): void {
        this.setSelectedTab(e.detail.id);
    }

    private setSelectedTab(id: string): void {
        const tabs: NodeListOf<HTMLDivElement> = this._node.querySelectorAll<HTMLDivElement>('.p-profile__tab');
        tabs.forEach((tab: HTMLDivElement) => {
            tab.classList.add('u-hidden');
        });
        this._node.querySelector<HTMLDivElement>(`.p-profile__tab-${Str.camelToKebab(id)}`).classList.remove('u-hidden');
        this.replaceState(`${location.pathname}?tab=${Str.camelToKebab(id)}`);
    }

    private loadPicture(): void {
        const file: File = this._fileElement.files[0];
        if (file) {
            const reader: FileReader = new FileReader();
            reader.onload = () => {            
                const fileExtension: string = file.name.split('.').reverse()[0];
                let fileContent: string = (reader.result as string).replace(/^data:(.*;base64,)?/, '');
                if (fileContent.length % 4 > 0) {
                    fileContent += '='.repeat(4 - (fileContent.length % 4));
                }
                this._fileElement.value = '';
                this.savePicture(fileContent, fileExtension);
            };
            reader.onerror = (error) => {
                logger.error(error);
                this.showUnexpectedError(error);
            };
            reader.readAsDataURL(file);
        }
    }

    private async savePicture(content: string, extension: string): Promise<void> {        
        const errors: string[] = this._validationService.validateAvatar(content);
        if (!(errors || []).length) {
            try {
                this.removeClass('u-hidden-important', '.p-profile__picture-loader');    
                const { url } = await this._apiService.post('/api/v1/user/picture', { 
                    body: {
                        content,
                        extension
                    }
                });                
                this._pictureElement.src = QueryString.addParamToUrl(`${CONFIG.STORAGE_URL}/${url}`, 'timestamp', Date.now().toString());        
                this.addClass('u-hidden-important', '.p-profile__signed-icon');
                this.removeClass('u-hidden-important', '.p-profile__image');
                this.removeClass('u-hidden-important', '.p-profile__remove-picture');        
                this.addClass('u-hidden-important', '.p-profile__picture-loader');
                this._sessionService.setPictureUrl(url);
            }
            catch (e: any) {
                this.addClass('u-hidden-important', '.p-profile__picture-loader');
                if (e.statusCode == 400) {
                    this.showInfo(USER_LOCALE.error, e.data?.message);
                }
                else {
                    this.showUnexpectedError(e);
                }
            }
        }
        else {
            this.showInfo(USER_LOCALE.error, errors.join('\n'));
        }
    }

    private async removePicture(): Promise<void> {        
        try {
            this.removeClass('u-hidden-important', '.p-profile__picture-loader');    
            await this._apiService.delete('/api/v1/user/picture');                
            this._pictureElement.src = '';
            this.removeClass('u-hidden-important', '.p-profile__signed-icon');
            this.addClass('u-hidden-important', '.p-profile__image');    
            this.addClass('u-hidden-important', '.p-profile__remove-picture');    
            this.addClass('u-hidden-important', '.p-profile__picture-loader');
            this._sessionService.setPictureUrl(null);
            this._fileElement.value = '';
        }
        catch (e: any) {
            this.addClass('u-hidden-important', '.p-profile__picture-loader');
            this.showUnexpectedError(e);
        }
    }

    @EventListener('selection-changed', '.m-disciplines-selector')
    public async onDisciplineClicked(e: CustomEvent<{ id: number, selected: boolean }>): Promise<void> {
        const { id, selected } = e.detail;
        if (!selected) {
            this._apiService.delete(`/api/v1/user/discipline/${id}`);

        }
        else {
            this._apiService.post(`/api/v1/user/discipline/${id}`);
        }
    }

    @ClickListener('#lnkSignOut')
    public async onSignOut(): Promise<void> {
        this.dispatchCustomWindowEvent('sign-out-request');
    }

    @ClickListener('#lnkSignOff')
    public async onSignOff(): Promise<void> {
        if (await this.showBasicQuestion(USER_LOCALE.signOff, USER_LOCALE.signOffQuestion, 'warning') == 'ok') {
            try {
                await this._apiService.delete('/api/v1/user');
                this._sessionService.remove();
                this.redirect('/');
            }
            catch (e: any) {
                if (e.statusCode == 409) {
                    this.showUnexpectedError({ message: e.data?.message });
                }
                else {
                    this.showUnexpectedError(e);
                }
            }
        }
    }

    @ClickListener('#btnChangePwd')
    public async onChangePassword(): Promise<void> {
        const result: boolean = await this._changePassword.open();
        if (result === true) {
            this._changePasswordConfirm.open();
        }
    }

    @ClickListener('#btnChangeProfile')
    public async onChangeProfile(): Promise<void> {
        const data: any = await this._changeProfile.open();
        if (data) {
            this.setProfileData(data);
        }
    }

    @ClickListener('#btnChangeCountry')
    public async onChangeCountry(): Promise<void> {
        const country: { id: number, name: string, flag: string, isDefault: boolean } = await this._countrySelector.open(this._country);
        if (country) {
            this.changeLocation(country, this._postalCode);
        }
    }

    @ClickListener('#btnCreatePin')
    public async onCreatePin(): Promise<void> {
        await this.createPin();
    }

    @ClickListener('#btnEditPin')
    public async onUpdatePin(): Promise<void> {
        const oldParentalPin: any = await this._enterPin.open();
        if (oldParentalPin) {
            await this.createPin(oldParentalPin);
        }
    }

    private async createPin(oldParentalPin?: string): Promise<void> {
        const button = document.getElementById('btnCreatePin');
        button?.setAttribute('aria-pressed', 'true');
        const parentalPin = await this._createPin.open(oldParentalPin);
        if (parentalPin) {
            this.setUserSetting('parental-control-with-pin', true, parentalPin);
            this.reload();
        }
    }

    private async changeLocation(country: { id: number, name: string }, postalCode: string): Promise<void> {
        try {
            await this._apiService.post(`/api/v1/user/setting/location/${country.id}${postalCode ? `/${postalCode}` : ''}`);
            this._country = country.id;
            this._postalCode = postalCode;
            this.setInnerHTML(country.name, '.p-profile__data-field-value--countryName');
            if (this._postalCode) {
                this._sessionService.setPostalCode(postalCode);
                this.setInnerHTML(this._postalCode, '.p-profile__data-field-value--postalCode');
                this.removeClass('u-hidden-important', '.p-profile__data-field--postalCode');
            }
            else {
                this.addClass('u-hidden-important', '.p-profile__data-field--postalCode');
            }
        }
        catch (e: any) {
            if (e.statusCode == 409) {
                if (e.data?.message == 'E-POSTAL-CODE-EMPTY') {
                    this.changeLocation(country, null);
                }
                else if (e.data?.message == 'E-POSTAL-CODE-REQUIRED') {
                    this.changePostalCode(country);
                }      
                else {                    
                    this.showUnexpectedError(e);
                }              
            }
            else if (e.statusCode == 400) {
                this.changePostalCode(country, e.data?.message ? JSON.parse(e.data.message): null);
            }
            else {
                this.showUnexpectedError(e);
            }
        }
    }

    @ClickListener('#btnChangePostalCode')
    public async onChangePostalCode(): Promise<void> {
        this.changePostalCode({ id: this._country, name: this.getInnerHTML('.p-profile__data-field-value--countryName') });
    }

    public async changePostalCode(country: { id: number, name: string}, errors?: DictionaryObject<string[]>): Promise<void> {
        const postalCode: string = await this._changePostalCode.open({ postalCode: this._postalCode, country: country.id, errors });
        if (postalCode) {
            this.changeLocation(country || { id: this._country, name: this.getInnerHTML('.p-profile__data-field-value--countryName') }, postalCode);       
        }
        else if (country.id != this._country) {
            this.showInfo(USER_LOCALE.operationCancelled, USER_LOCALE.dataNotUpdated);
        }
    }

    @EventListener('click', `${Object.keys(CONFIG.ALLOWED_LOCALES).map((locale, index) => `#rb-locale-${index}`).join(', ')}`)
    public async onChangeLanguage(e: CustomEvent): Promise<void> {
        const locale: string = ((e.target as HTMLElement).closest('.p-profile__data-locale-item') as HTMLDivElement)?.dataset.locale;
        try {
            await this._apiService.post(`/api/v1/user/setting/language/${locale}`);
            this._sessionService.setLanguage(locale);
            window.location.reload();
        }
        catch (e: any) {
            this.showUnexpectedError(e);
        }
    }

    @EventListener('change', '.a-switch-box')
    public async onChangeOption(e: CustomEvent): Promise<void> {
        const switchBox: SwitchBoxComponent = e.detail;
        if (switchBox) {
            const id: string = switchBox.node.querySelector('input').id;
            switch (id) {
                case 'sbAutoplay':
                    this.setUserSetting('autoplay', switchBox.value);
                    break;
                case 'sbParentControl':
                    if (switchBox.value) {
                        this.setUserSetting('parental-control', switchBox.value);
                    } else {
                        const parentalPin = await this._enterPin.open();
                        if (parentalPin) {
                            this.setUserSetting('parental-control-with-pin', switchBox.value, parentalPin);
                            this.reload();
                        }
                        else {
                            switchBox.value = !switchBox.value;
                        }
                    }
                    break;
            }
        }
    }

    private async setUserSetting(name: string, value: boolean, optionalValue?: string): Promise<void> {
        try {
            await this._apiService.post(`/api/v1/user/setting/${name}/${value}${!!optionalValue ? `/${optionalValue}`:``}`);
        }
        catch (e: any) {
            this.showUnexpectedError(e);
        }
    }

    private updateTabOrientation(query: MediaQueryList | MediaQueryListEvent): void {
        this._tab.setOrientation(query.matches ? 'horizontal' : 'vertical');
    }

    private setProfileData(data: any): void {
        const { fullname, gender, birthDate } = data;
        this.setInnerHTML(fullname, '.p-profile__data-field-value--fullname');
        this.setInnerHTML(gender, '.p-profile__data-field-value--gender');
        this.setInnerHTML(birthDate, '.p-profile__data-field-value--birthDate');
        this.dispatchCustomEvent('profile-updated', data);
    }

    public dispose(): void {
        this._verticalTabsQuery.removeEventListener('change', e => this.updateTabOrientation(e));
        super.dispose();
    }
}